import './styles/VentingArchives.scss';

import {
    ImageBlock,
    Listing,
    Pagination,
    TitleBanner,
} from '../components/Primitives';
import React, { useEffect, useState } from 'react';

import { IProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/engine/PricesAndInventoriesCache';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductsPrices } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/queries/useProductsPriceAndInventories';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($category: String!, $cms: String!) {
        listing: allProductsJson(filter: { category: { regex: $category } }) {
            nodes {
                code
                id
                documents {
                    en_CA
                    en_US
                    fr_CA
                    caption {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                description {
                    valuesMarkdownRemark {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {                   
                    diameter {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        tuyauxCms: allProductsJson(filter: { code: { eq: $cms } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        listingTuyau: allProductsJson(
            filter: { code: { eq: "drolet_listing-tuyau" } }
        ) {
            nodes {
                images
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_1 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_2 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function VentingArchives({ data, location, pageContext }) {
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('url');
    const allProducts = data.listing.nodes;
    const cms = data?.tuyauxCms.nodes;
    const typeArg = t(`${pageContext.typeArg}`);
    const cmsBloc = data?.listingTuyau?.nodes[0];
    const pageTitle = cms?.[0]?.title?.[currentLocale];
    const responsiveImages = cms?.[0]?.fields;

    const desktopImage =
        responsiveImages?.banniere_desktop?.[0]?.childImageSharp
            ?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile?.[0]?.childImageSharp
            ?.gatsbyImageData;

    const isMobile = useMobileDetect().isMobile();
    const [page, setPage] = useState(1);
    const APPLIANCE_PER_PAGE = isMobile ? 6 : 12;
    const metadata = {
        title: cms?.[0]?.attributes?.meta_title?.values[0][currentLocale],
        description:
            cms?.[0]?.attributes?.meta_description?.values[0][currentLocale],
    };
    const allCategoryMap = getCategoryMap(allProducts, currentLocale);
    const [selectedArgs] = useQueryArg('category');
    const [diameterArg] = useQueryArg('diameter');
    const [filteredProducts, setFilteredProducts] = useState(allProducts);
    const [filterDiameterState, setFilterDiameterState] = useState(
        diameterArg ? diameterArg : ''
    );
    const [filterSelectedState, setFilterSelectedState] = useState(
        selectedArgs ? selectedArgs.split(',') : []
    );
    const prices = useProductsPrices(
        allProducts
            .filter((e) => e?.slug?.[currentLocale].includes(typeArg))
            .map((family: any) => family.code)
    );

    if (location && typeof window !== 'undefined')
        window.scrollTo(0, location?.state?.scrollPosition);

    useEffect(() => {
        if (selectedArgs) {
            setFilterSelectedState(selectedArgs.split(','));
        } else {
            setFilterSelectedState([]);
        }
        if (diameterArg) {
            setFilterDiameterState(diameterArg);
        } else {
            setFilterDiameterState('');
        }
        setPage(1);
    }, [diameterArg, selectedArgs]);

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <TitleBanner
                className='venting-listing-titlebanner'
                imageData={isMobile ? mobileImage : desktopImage}
                title={pageTitle}
            />
            <div className='venting-listing-page'>
                {typeArg !== 'top-dampers' && (
                    <VentingFilter
                        allCategoryMap={allCategoryMap}
                        setFilteredProducts={setFilteredProducts}
                        allProducts={allProducts}
                        ventingType={typeArg}
                        filterDiameterState={filterDiameterState}
                        filterSelectedState={filterSelectedState}
                        diameterArg={diameterArg}
                        selectedArgs={selectedArgs}
                        prices={prices}
                    />
                )}
                <Listing
                    items={
                        filteredProducts &&
                        filteredProducts.length > 0 &&
                        filteredProducts?.slice(
                            APPLIANCE_PER_PAGE * (page - 1),
                            APPLIANCE_PER_PAGE * page
                        )
                    }
                    displayLink={true}
                    displayManual={false}
                    hasOwnPage={true}
                    displayDescription={true}
                />
            </div>
            {filteredProducts &&
                filteredProducts.length / APPLIANCE_PER_PAGE > 1 && (
                    <Pagination
                        currentPage={page}
                        onPageChange={(newPage: number) => setPage(newPage)}
                        pageNumber={Math.ceil(
                            filteredProducts.length / APPLIANCE_PER_PAGE
                        )}
                    />
                )}
            <ImageBlock
                subtitle={
                    cmsBloc?.attributes?.texte_1?.values[0]?.[currentLocale]
                }
                title={cmsBloc?.attributes?.texte_2?.values[0]?.[currentLocale]}
                image={
                    cmsBloc?.fields?.images?.[0]?.childImageSharp
                        ?.gatsbyImageData
                }
                content={
                    cmsBloc?.attributes?.content?.values[0]?.[currentLocale]
                }
            />
        </Layout>
    );
}
function VentingFilter(props: {
    allCategoryMap: Map<any, any>;
    setFilteredProducts: React.Dispatch<any>;
    allProducts: any[];
    ventingType: string;
    filterDiameterState: string;
    filterSelectedState: string[];
    diameterArg: string;
    selectedArgs: string;
    prices: readonly [IProductPrice[]];
}) {
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const {
        allCategoryMap,
        allProducts,
        setFilteredProducts,
        ventingType,
        filterDiameterState,
        filterSelectedState,
        diameterArg,
        selectedArgs,
        prices,
    } = props;

    useEffect(() => {
        let prod = allProducts.filter((e) =>
            e?.slug?.[currentLocale].includes(ventingType)
        );

        prod = prod.filter((e) => {
            if (diameterArg?.length === 0) return true;

            return diameterArg.includes(
                // On compare avec le premier chiffre seulement pour ignorer le \" que Studio retourne
                e?.attributes?.diameter?.values[0]['fr_CA'][0]
            );
        });

        prod = prod.filter((e) => {
            if (filterSelectedState.length > 0) {
                let flag = false;
                filterSelectedState.forEach((state) => {
                    if (e?.slug?.[currentLocale].includes(state)) {
                        flag = true;
                    }
                });
                return flag;
            } else {
                return true;
            }
        });
        setFilteredProducts(prod);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentLocale,
        setFilteredProducts,
        ventingType,
        filterDiameterState,
        filterSelectedState,
        allProducts,
    ]);

    let diameters: string[];
    if (ventingType === t('url:flex-liner')) {
        diameters = ['4', '5', '6'];
    } else if (
        ventingType === t('url:double-wall-stove-pipe') ||
        ventingType === t('url:single-wall-stove-pipe')
    ) {
        diameters = ['6', '7', '8'];
    } else if (ventingType === t('url:pellet-vent')) {
        diameters = ['3', '4'];
    } else {
        diameters = [];
    }

    return (
        <div className='venting-listing-filter'>
            <h1 className='venting-filter-title'>{t('venting.filter')}</h1>

            {RenderCategoryFilter(
                allCategoryMap,
                ventingType,
                navigateTranslated,
                selectedArgs,
                diameterArg
            )}

            {diameters?.length > 0 &&
                RenderDiameterFilter(
                    navigateTranslated,
                    ventingType,
                    diameters,
                    diameterArg,
                    selectedArgs
                )}

            <div className='venting-filter-category'>
                <span
                    className='venting-reset-filter'
                    onClick={() => {
                        const scrollPosition =
                            document.documentElement.scrollTop ||
                            document.body.scrollTop;
                        navigateTranslated(
                            `/venting/${ventingType}`,
                            scrollPosition
                        );
                    }}
                >
                    {t('venting.reset-filter')}
                </span>
            </div>
        </div>
    );
}
function getCategoryMap(products: any, currentLocale: string) {
    const categoryMap = new Map();
    products.forEach((product) => {
        const re = /-/gi;
        const categoryLocalized: string[] =
            product.category_localized[currentLocale].split('/');
        const ventingCategory = categoryLocalized[3].replace(re, ' ');
        const ventingSubCategoty = categoryLocalized[4];
        const slug: string[] = product.slug[currentLocale].split('/');
        const slugValue = slug[2] + '/' + slug[3];
        const collection = categoryMap.get(ventingCategory);
        if (!collection) {
            categoryMap.set(ventingCategory, [
                { label: ventingSubCategoty, slug: slugValue },
            ]);
        } else {
            if (!collection.find((e) => e.label === ventingSubCategoty)) {
                collection.push({ label: ventingSubCategoty, slug: slugValue });
                collection.sort((a, b) => a?.label?.localeCompare(b?.label));
            }
        }
    });
    return categoryMap;
}
function RenderCategoryFilter(
    allCategoryMap: Map<string, any>,
    ventingType: string,
    navigateTranslated: (url: string, scrollPosition?: number) => void,
    categoryArgs,
    diameterArgs
): any {
    const returnArray = [];
    if (ventingType) {
        const re = / /gi;
        const reA = /à/gi;
        for (const [key, value] of allCategoryMap) {
            const currentType = key
                .toLowerCase()
                .replace(re, '-')
                .replace(reA, 'a');
            if (ventingType.toLocaleLowerCase() === currentType) {
                returnArray.push(
                    <div className='venting-filter-category' key={key}>
                        <h3 className='venting-section-title'>{key}</h3>
                        <div className='filter-checkbox-group'>
                            {value.map((single, index) => {
                                return (
                                    <SingleCheckBox
                                        key={key + index}
                                        preId={'venting-filter-categories-'}
                                        index={index}
                                        single={single}
                                        checkFlag={categoryArgs?.includes(
                                            single?.slug
                                                ?.replace(re, '-')
                                                ?.split('/')[1]
                                        )}
                                        onChange={(checked) => {
                                            const scrollPosition =
                                                document.documentElement
                                                    .scrollTop ||
                                                document.body.scrollTop;
                                            const value = single?.slug?.replace(
                                                re,
                                                '-'
                                            );
                                            if (checked) {
                                                const oldCategory = `${
                                                    categoryArgs
                                                        ? categoryArgs + ','
                                                        : ''
                                                }`;
                                                const oldDiameter = `${
                                                    diameterArgs
                                                        ? '?diameter=' +
                                                          diameterArgs
                                                        : ''
                                                }`;

                                                if (
                                                    !oldCategory.includes(
                                                        value?.split('/')[1]
                                                    )
                                                ) {
                                                    const categoryRedirect =
                                                        value?.split('/')[1];
                                                    navigateTranslated(
                                                        `/venting/${ventingType}/${oldDiameter}${
                                                            oldDiameter?.length >
                                                            0
                                                                ? '&'
                                                                : '?'
                                                        }category=${
                                                            oldCategory +
                                                            categoryRedirect
                                                        }`,
                                                        scrollPosition
                                                    );
                                                }
                                            } else {
                                                const oldCategory = `${
                                                    categoryArgs
                                                        ? categoryArgs
                                                        : ''
                                                }`;
                                                const oldDiameter = `${
                                                    diameterArgs
                                                        ? '?diameter=' +
                                                          diameterArgs
                                                        : ''
                                                }`;
                                                if (
                                                    oldCategory.includes(
                                                        value?.split('/')[1]
                                                    )
                                                ) {
                                                    let val =
                                                        oldCategory
                                                            ?.split(',')
                                                            ?.filter(
                                                                (e) =>
                                                                    e !==
                                                                    value?.split(
                                                                        '/'
                                                                    )[1]
                                                            )
                                                            ?.join() || '';
                                                    val =
                                                        val.length > 0
                                                            ? 'category=' + val
                                                            : val;
                                                    navigateTranslated(
                                                        `/venting/${ventingType}${oldDiameter}${
                                                            oldDiameter?.length >
                                                            0
                                                                ? '&'
                                                                : '?'
                                                        }${val}`,
                                                        scrollPosition
                                                    );
                                                }
                                            }
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            }
        }
    }
    return returnArray;
}
function RenderDiameterFilter(
    navigateTranslated: (url: string, scrollPosition?: number) => void,
    ventingType: string,
    diameters: string[],
    diameterArg,
    categoryArgs
): any {
    const { t } = useTranslation('layout');

    return (
        <div className='venting-filter-category'>
            <h3 className='venting-section-title'>{t('venting.diameter')}</h3>
            <div className='filter-checkbox-group'>
                {diameters?.map((diameter, index) => {
                    return (
                        <SingleCheckBox
                            key={`diameter-${diameter}`}
                            preId={`venting-filter-diameter-${diameter}`}
                            index={index}
                            single={{ label: `${diameter}"` }}
                            checkFlag={diameterArg.includes(diameter)}
                            onChange={(checked) => {
                                const scrollPosition =
                                    document.documentElement.scrollTop ||
                                    document.body.scrollTop;

                                if (checked) {
                                    const oldDiameter = `${
                                        diameterArg ? diameterArg + ',' : ''
                                    }`;

                                    const categoryArgText =
                                        categoryArgs.length > 0
                                            ? `&category=${categoryArgs}`
                                            : '';

                                    navigateTranslated(
                                        `/venting/${ventingType}/?diameter=${
                                            oldDiameter + diameter
                                        }${categoryArgText}`,
                                        scrollPosition
                                    );
                                } else {
                                    let oldDiameter = `${diameterArg
                                        ?.split(',')
                                        ?.filter((diam) => diam !== diameter)
                                        ?.join(',')}`;

                                    oldDiameter =
                                        oldDiameter.length > 0
                                            ? `?diameter=${oldDiameter}`
                                            : '';

                                    const categoryArgText =
                                        categoryArgs.length > 0
                                            ? `category=${categoryArgs}`
                                            : '';

                                    navigateTranslated(
                                        `/venting/${ventingType}/${oldDiameter}${
                                            oldDiameter.length > 0 ? '&' : '?'
                                        }${categoryArgText}`,
                                        scrollPosition
                                    );
                                }
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function SingleCheckBox(props: {
    index: number;
    single: any;
    onChange: (checked: boolean) => void;
    preId: string;
    checkFlag?: boolean;
}) {
    const { index, single, onChange, preId, checkFlag } = props;
    const [isChecked, setChecked] = useState(false);
    useEffect(() => {
        if (!checkFlag) {
            setChecked(false);
        } else {
            setChecked(true);
        }
    }, [checkFlag]);
    return (
        <div className='checkbox-and-label' key={index}>
            <input
                id={`${preId}${index}`}
                type='checkbox'
                onChange={(event) => {
                    if (onChange) {
                        onChange(event.target.checked);
                        setChecked(event.target.checked);
                    }
                }}
                checked={isChecked}
            />
            <label htmlFor={`${preId}${index}`} className='checkbox-label'>
                {single.label}
            </label>
        </div>
    );
}

function filterByPrice(prices: any, filterState: string, index: number) {
    if (!filterState || filterState === '') {
        return true;
    }
    const valueArr = filterState.split('-');
    let max: any = null;
    let min: any = null;
    valueArr.forEach((e) => {
        try {
            if (!isNaN(parseInt(e))) {
                const number = parseInt(e);
                if (min === null || number <= min) {
                    min = number;
                }
                if (max === null || number >= max) {
                    max = number;
                }
            }
        } catch (e) {
            console.error('error catched: VentingArchives');
            console.error(e);
        }
    });
    const regularPrice: number = prices[0][index]?.regularPrice?.value
        ? prices[0][index]?.regularPrice?.value
        : 0;
    if (
        min === null ||
        max === null ||
        (regularPrice <= max && regularPrice >= min)
    ) {
        return true;
    }
    return false;
}
